import { memo, useCallback, useState, useRef, useId } from 'react'
import { Check, SwapVert } from '@mui/icons-material';
import { ListItemIcon, Menu, MenuItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import BottomMenu from "./BottomMenu";
import PodcastEpisodes from "./PodcastEpisodes";
import PodcastHeader from "./PodcastHeader";
import SearchBoxWithDefaultSuggestions from "./SearchBoxWithDefaultSuggestions";
import AlgoliaPodcastSearch from './AlgoliaPodcastSearch';
import SearchSuggestions from './SearchSuggestions';
import useEntitySearchSuggestions from './useEntitySearchSuggestions';
import TopBar from "./TopBar";
import FullWidthButton from './FullWidthButton';
import HighlightsDrawer from './HighlightsDrawer';
import { SearchActivityProvider } from './SearchActivity';
import SegmentedTabs from './SegmentedTabs';

// Changing the results category must not recreate autocomplete and discard a draft query.
const PodcastSearchInput = memo(SearchBoxWithDefaultSuggestions);

const PodcastEpisodeSection = ({ podcastId }) => {
    const [sortOrder, setSortOrder] = useState('newest');
    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuId = useId();
    const sortTitle = sortOrder === 'newest' ? 'Newest' : 'Oldest';

    return (
        <>
            <div className='entityInfoSectionTitle entityEpisodeSectionTitle podcastEpisodeHeading'>
                <span>Episodes</span>
                <button
                    type='button'
                    className='podcastEpisodeSortButton'
                    aria-label={`Sort episodes: ${sortTitle} first`}
                    aria-haspopup='menu'
                    aria-expanded={!!menuAnchor}
                    aria-controls={menuAnchor ? menuId : undefined}
                    onClick={event => setMenuAnchor(event.currentTarget)}
                >
                    <SwapVert fontSize='small' />
                    {sortTitle}
                </button>
                {/* Keep the portal stable when clearing the anchor during its exit transition. */}
                <Menu
                    id={menuId}
                    container={() => document.body}
                    anchorEl={menuAnchor}
                    open={!!menuAnchor}
                    onClose={() => setMenuAnchor(null)}
                    MenuListProps={{ 'aria-label': 'Sort episodes' }}
                >
                    {['newest', 'oldest'].map(order => (
                        <MenuItem
                            key={order}
                            role='menuitemradio'
                            aria-checked={sortOrder === order}
                            selected={sortOrder === order}
                            onClick={() => {
                                setSortOrder(order);
                                setMenuAnchor(null);
                            }}
                        >
                            <ListItemIcon>{sortOrder === order && <Check fontSize='small' />}</ListItemIcon>
                            {order === 'newest' ? 'Newest first' : 'Oldest first'}
                        </MenuItem>
                    ))}
                </Menu>
            </div>
            <div className='entityInfoContent entityEpisodeList'>
                <PodcastEpisodes podcastId={podcastId} sortOrder={sortOrder} />
            </div>
        </>
    );
};

const PodcastView = ({ podcast }) => {

    const { suggestions: defaultSuggestions, isLoading: suggestionsLoading } = useEntitySearchSuggestions(
        podcast?.id ? `/podcast_search_suggestions/${podcast.id}` : null,
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const searchContainerRef = useRef(null);
    const [showHighlights, setShowHighlights] = useState(false);
    const [highlightSearchEntry, setHighlightSearchEntry] = useState(0);

    const getCleanedQuery = (params) => {
        const rawQuery = params.get('q');
        return rawQuery ? rawQuery.trim() : '';
    }
    const query = getCleanedQuery(searchParams);


    const missingPodcast = (
        <div>
            <TopBar />

            <div style={{ textAlign: 'center' }}>
                <div style={{ padding: '60px 20px' }}>
                    <div style={{ fontSize: '18px', fontWeight: '500' }}>
                        Show not found.
                    </div>
                </div>
            </div>
        </div>
    )



    const querySelected = useCallback((newQuery) => {
        const nextQuery = (newQuery || '').trim();
        const nextSearchParams = new URLSearchParams(searchParams);
        document.getElementById('searchContainer')?.scrollTo(0, 0)
        if (nextQuery) {
            nextSearchParams.set('q', nextQuery);
        } else {
            nextSearchParams.delete('q');
        }
        setSearchParams(nextSearchParams);
    }, [searchParams, setSearchParams]);

    const highlightQuerySelected = (newQuery) => {
        setHighlightSearchEntry(entry => entry + 1);
        querySelected(newQuery);
    };

    const noQueryView = !podcast ? null : (() => {
        const hasHighlights = !!podcast?.previews?.length;

        return (
            <div>
                <PodcastHeader podcast={podcast} />
                {hasHighlights && (
                    <div className='entityHighlightsActionGroup'>
                        <FullWidthButton
                            title='Highlights'
                            accessibilityLabel='Browse highlights'
                            onClick={() => setShowHighlights(true)}
                            buttonClassName='entityHighlightsActionRow'
                        />
                    </div>
                )}
                <SearchSuggestions key={`topics:${podcast.id}`} querySelected={highlightQuerySelected} defaultSuggestions={defaultSuggestions} isLoading={suggestionsLoading} />
                <PodcastEpisodeSection key={podcast.id} podcastId={podcast.id} />
            </div>
        );
    })();

    return (
        <div className="container appScreen" style={query ? { overflow: 'visible' } : undefined}>
            <HighlightsDrawer
                open={showHighlights}
                onClose={() => setShowHighlights(false)}
                onSearch={() => {
                    setShowHighlights(false);
                    setHighlightSearchEntry(entry => entry + 1);
                    requestAnimationFrame(() => searchContainerRef.current?.querySelector('.aa-Input')?.focus());
                }}
                previews={podcast?.previews || []}
                previewTitle={podcast?.name || 'Highlights'}
                playbackOrigin={{ kind: 'podcast', id: podcast?.id, name: podcast?.name, imageURL: podcast?.imageURL }}
            />
            <div className='hasBottomMenu'>
                {!!podcast ?
                    <SearchActivityProvider>
                        <div id="searchContainer" ref={searchContainerRef}>
                            <AlgoliaPodcastSearch
                                podcast={podcast}
                                noQueryView={noQueryView}
                                initialQuery={query}
                                initialResultsTab='highlights'
                                searchIntentKey={highlightSearchEntry}
                                renderSearchInput={({ options, ...selection }) => (
                                    <div
                                        className='algoliaCustom appSearchBoxDock fixToTop podcastSearchBoxDock'
                                        style={{ '--podcast-search-controls-height': options.length > 1 ? '52px' : '0px' }}
                                    >
                                        <PodcastSearchInput
                                            key={`${podcast.id}-${query || 'empty'}`}
                                            querySelected={querySelected}
                                            defaultSuggestions={defaultSuggestions}
                                            placeholder={options.length === 1
                                                ? `Search episodes in ${podcast.name}` : `Search ${podcast.name}`}
                                            initialQuery={query}
                                        />
                                        {options.length > 1 && (
                                            <div className='podcastSearchControls' onMouseDown={event => event.preventDefault()}>
                                                <SegmentedTabs options={options} {...selection} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </SearchActivityProvider> :
                    missingPodcast
                }
                <BottomMenu />
            </div>
        </div >
    );
};

export default PodcastView;
